import { motion } from "framer-motion";
import { Sparkles, ExternalLink, Globe, LayoutGrid, Zap } from "lucide-react";
import { Layout } from "@/components/Layout";
import { useLanguage } from "@/lib/LanguageContext";
import { useProfile } from "@/lib/ProfileContext";
import { springPresets } from "@/lib/motion";
import { EXTERNAL_LINKS } from "@/lib/index";

export default function StudioPage() {
  const { t } = useLanguage();
  const { profile } = useProfile();

  // Construct deep link with pre-filled data
  const getStudioUrl = () => {
    const url = new URL(EXTERNAL_LINKS.CREATIVE_STUDIO);
    if (profile.fullName) url.searchParams.set("name", profile.fullName);
    if (profile.headline) url.searchParams.set("headline", profile.headline);
    if (profile.position) url.searchParams.set("role", profile.position);
    if (profile.industry) url.searchParams.set("industry", profile.industry);
    if (profile.skills) url.searchParams.set("skills", profile.skills);
    // Map 'about' to 'bio' or similar if target app supports it
    if (profile.about) url.searchParams.set("bio", profile.about.substring(0, 500)); 
    return url.toString();
  };

  const studioUrl = getStudioUrl();

  return (
    <Layout>
      <div className="h-[calc(100vh-120px)] flex flex-col">
        {/* Header Section */}
        <div className="max-w-7xl mx-auto px-4 py-4 w-full flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground leading-tight">{t("studio.title")}</h1>
              <p className="text-xs text-muted-foreground">{t("studio.subtitle")}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <a
              href={studioUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted/50 hover:bg-muted text-xs font-semibold transition-all border border-border/50"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {t("common.openNewTab")}
            </a>
          </div>
        </div>

        {/* Embedded App Container */}
        <div className="flex-1 w-full bg-black/20 relative overflow-hidden">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="w-full h-full"
          >
            <iframe
              src={studioUrl}
              className="w-full h-full border-0"
              title="Creative Studio"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </motion.div>
          
          {/* Overlay info if needed, but iframe is primary */}
          <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
            <div className="max-w-fit mx-auto px-4 py-2 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-[10px] text-white/40 flex items-center gap-2">
              <Zap className="w-3 h-3" />
              {t("common.poweredBy")}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
